import { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Activity, Search, User, Calendar, Filter,
  LogIn, LogOut, FileText, Package, CreditCard, 
  Bus, MapPin, Users, RefreshCw, AlertTriangle
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ActivityLogsPage = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('');

  useEffect(() => {
    if (token) {
      fetchActivityLogs();
    }
  }, [token]);

  const fetchActivityLogs = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/users/activity`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle both array response and object response with users/recent_activity
      if (Array.isArray(response.data)) {
        setLogs(response.data);
      } else {
        setUsers(response.data.users || []);
        setLogs(response.data.recent_activity || []);
      }
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to load activity logs');
      }
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    const icons = {
      'LOGIN': LogIn,
      'LOGOUT': LogOut,
      'CREATE_BOOKING': FileText,
      'CANCEL_BOOKING': AlertTriangle,
      'CHECKIN': Users,
      'BOARD': Bus,
      'CREATE_LUGGAGE': Package,
      'UPDATE_LUGGAGE': Package,
      'PAYMENT': CreditCard,
      'CREATE_EXPENSE': CreditCard,
      'APPROVE_EXPENSE': CreditCard,
      'START_TRIP': MapPin,
      'END_TRIP': MapPin,
      'PRINT_TICKET': FileText,
      'CREATE_USER': User,
      'UPDATE_USER': User,
      'DELETE_USER': User,
    };
    return icons[action] || Activity;
  };

  const getActionBadgeColor = (action) => {
    if (action.includes('CREATE') || action.includes('APPROVE')) {
      return 'bg-green-100 text-green-700';
    }
    if (action.includes('DELETE') || action.includes('CANCEL') || action.includes('REJECT')) {
      return 'bg-red-100 text-red-700';
    }
    if (action.includes('UPDATE')) {
      return 'bg-blue-100 text-blue-700';
    }
    if (action.includes('LOGIN') || action.includes('LOGOUT')) {
      return 'bg-purple-100 text-purple-700';
    }
    return 'bg-slate-100 text-slate-700';
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.entity_type?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = !filterAction || log.action === filterAction;
    
    return matchesSearch && matchesFilter;
  });

  const uniqueActions = [...new Set(logs.map(log => log.action))].sort();

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="activity-logs-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
              User Activity Logs
            </h1>
            <p className="text-slate-500 mt-1">
              Monitor staff actions and system activities
            </p>
          </div>
          <Button onClick={fetchActivityLogs} variant="outline" data-testid="refresh-logs-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Staff Summary Cards */}
        {users.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="card-panashe">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Users className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{users.length}</p>
                    <p className="text-sm text-slate-500">Active Staff</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-panashe">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Activity className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{logs.length}</p>
                    <p className="text-sm text-slate-500">Total Actions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-panashe">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <LogIn className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">
                      {logs.filter(l => l.action === 'LOGIN').length}
                    </p>
                    <p className="text-sm text-slate-500">Logins Today</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-panashe">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-panashe-gold/20 rounded-full flex items-center justify-center">
                    <FileText className="h-5 w-5 text-panashe-blue" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">
                      {logs.filter(l => l.action === 'CREATE_BOOKING').length}
                    </p>
                    <p className="text-sm text-slate-500">Bookings</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="card-panashe">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by user, action, or entity..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-logs-input"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <select
                  value={filterAction}
                  onChange={(e) => setFilterAction(e.target.value)}
                  className="px-3 py-2 border rounded-md text-sm"
                  data-testid="filter-action-select"
                >
                  <option value="">All Actions</option>
                  {uniqueActions.map(action => (
                    <option key={action} value={action}>{action.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Activity Logs Table */}
        <Card className="card-panashe">
          <CardHeader className="border-b">
            <CardTitle className="font-heading flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Activity Timeline ({filteredLogs.length} entries)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="spinner mx-auto mb-4" />
                <p className="text-slate-500">Loading activity logs...</p>
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Activity className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>No activity logs found</p>
              </div>
            ) : (
              <div className="divide-y max-h-[600px] overflow-y-auto">
                {filteredLogs.map((log, index) => {
                  const ActionIcon = getActionIcon(log.action);
                  return (
                    <div 
                      key={log.id || index} 
                      className="p-4 hover:bg-slate-50 transition-colors"
                      data-testid={`log-entry-${index}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${getActionBadgeColor(log.action)}`}>
                          <ActionIcon className="h-5 w-5" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-slate-900">
                              {log.user_name || 'System'}
                            </span>
                            <Badge className={getActionBadgeColor(log.action)}>
                              {log.action?.replace('_', ' ')}
                            </Badge>
                            <span className="text-slate-500">
                              {log.entity_type}
                            </span>
                          </div>
                          
                          {log.details && Object.keys(log.details).length > 0 && (
                            <p className="text-sm text-slate-500 mt-1 truncate">
                              {JSON.stringify(log.details).slice(0, 100)}...
                            </p>
                          )}
                          
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {log.timestamp || log.created_at ? 
                                format(parseISO(log.timestamp || log.created_at), 'MMM d, yyyy HH:mm:ss') : 
                                'Unknown'
                              }
                            </span>
                            {log.entity_id && (
                              <span className="font-mono">
                                ID: {log.entity_id.slice(0, 8)}...
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ActivityLogsPage;
